import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import PaletteIcon from '@mui/icons-material/Palette';
import WebIcon from '@mui/icons-material/Web';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import websiteSettingsService from '../../services/websiteSettingsService';

const WebsiteSettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState({
    primaryColor: '#1a237e',
    secondaryColor: '#c51162',
    logoPosition: 'LEFT',
    title: '',
    tagline: '',
    aboutText: '',
    shippingPolicy: '',
    returnsPolicy: '',
    isEnabled: false
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Load current settings
  useEffect(() => {
    loadSettings();
  }, []);

  // Check for changes
  useEffect(() => {
    const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await websiteSettingsService.getWebsiteSettings();
      const loadedSettings = {
        primaryColor: data.primaryColor || '#1a237e',
        secondaryColor: data.secondaryColor || '#c51162',
        logoPosition: data.logoPosition || 'LEFT',
        title: data.title || '',
        tagline: data.tagline || '',
        aboutText: data.aboutText || '',
        shippingPolicy: data.shippingPolicy || '',
        returnsPolicy: data.returnsPolicy || '',
        isEnabled: data.isEnabled !== undefined ? data.isEnabled : false
      };
      setSettings(loadedSettings);
      setOriginalSettings(JSON.parse(JSON.stringify(loadedSettings)));
    } catch (error) {
      enqueueSnackbar('Failed to load website settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await websiteSettingsService.updateWebsiteSettings(settings);
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      setHasChanges(false);
      enqueueSnackbar('Website settings saved successfully!', { variant: 'success' });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save website settings';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            underline="hover"
          >
            Dashboard
          </Link>
          <Typography color="text.primary">Website Settings</Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WebIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Website Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Customize your e-commerce website appearance, colors, and content
          </Typography>
        </Box>

        {/* Enable/Disable Toggle */}
        <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.isEnabled}
                onChange={handleInputChange('isEnabled')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Enable E-commerce Website
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When enabled, your website will be accessible at your configured domain path
                </Typography>
              </Box>
            }
          />
        </Paper>

        {/* Settings Form */}
        <Paper elevation={2}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="website settings tabs">
              <Tab label="Appearance" icon={<PaletteIcon />} iconPosition="start" />
              <Tab label="Content" icon={<WebIcon />} iconPosition="start" />
            </Tabs>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {/* Appearance Tab */}
            {tabValue === 0 && (
              <Grid container spacing={3}>
                {/* Color Settings */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Color Scheme
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Primary Color"
                      type="color"
                      value={settings.primaryColor}
                      onChange={handleInputChange('primaryColor')}
                      InputLabelProps={{ shrink: true }}
                      helperText="Main brand color used for buttons, links, and highlights"
                    />
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 60,
                      backgroundColor: settings.primaryColor,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 600
                    }}
                  >
                    Primary Color Preview
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Secondary Color"
                      type="color"
                      value={settings.secondaryColor}
                      onChange={handleInputChange('secondaryColor')}
                      InputLabelProps={{ shrink: true }}
                      helperText="Accent color for hover states and secondary elements"
                    />
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 60,
                      backgroundColor: settings.secondaryColor,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 600
                    }}
                  >
                    Secondary Color Preview
                  </Box>
                </Grid>

                {/* Logo Position */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Layout Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Logo Position</InputLabel>
                    <Select
                      value={settings.logoPosition}
                      onChange={handleInputChange('logoPosition')}
                      label="Logo Position"
                    >
                      <MenuItem value="LEFT">Left</MenuItem>
                      <MenuItem value="CENTER">Center</MenuItem>
                      <MenuItem value="RIGHT">Right</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {/* Content Tab */}
            {tabValue === 1 && (
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Basic Information
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Website Title"
                    value={settings.title}
                    onChange={handleInputChange('title')}
                    placeholder="e.g., Welcome to Our Store"
                    helperText="Main heading displayed on your website homepage (e.g., 'Welcome to emi residency')"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tagline / Slogan"
                    value={settings.tagline}
                    onChange={handleInputChange('tagline')}
                    placeholder="e.g., Discover our premium collection of products selected just for you"
                    helperText="Short description or slogan displayed below the title"
                    multiline
                    rows={2}
                  />
                </Grid>

                {/* Content Sections */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Content Sections
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="About Us"
                    value={settings.aboutText}
                    onChange={handleInputChange('aboutText')}
                    placeholder="Tell customers about your company..."
                    helperText="This content appears in the 'About Us' dialog on your website"
                    multiline
                    rows={6}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Shipping Policy"
                    value={settings.shippingPolicy}
                    onChange={handleInputChange('shippingPolicy')}
                    placeholder="Describe your shipping policies, delivery times, and costs..."
                    helperText="This content appears in the 'Shipping Policy' dialog on your website"
                    multiline
                    rows={6}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Returns & Exchanges Policy"
                    value={settings.returnsPolicy}
                    onChange={handleInputChange('returnsPolicy')}
                    placeholder="Describe your return and exchange policies..."
                    helperText="This content appears in the 'Returns & Exchanges' dialog on your website"
                    multiline
                    rows={6}
                  />
                </Grid>
              </Grid>
            )}

            {/* Save Button */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || !hasChanges}
                size="large"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>

            {hasChanges && (
              <Alert severity="info" sx={{ mt: 2 }}>
                You have unsaved changes. Don't forget to save!
              </Alert>
            )}
          </CardContent>
        </Paper>
      </Container>
    </Layout>
  );
};

export default WebsiteSettings;

